
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activityFeedService, ActivityFilters } from '@/services/activity';
import ActivityItem from './ActivityItem';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Filter, ChevronDown, ChevronUp } from 'lucide-react';

interface ActivityFeedProps {
  familyId: string;
  familyMembers?: Array<{ id: string; first_name: string; last_name: string; }>;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ familyId, familyMembers = [] }) => {
  const [filters, setFilters] = useState<ActivityFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data: response, isLoading, error, refetch } = useQuery({
    queryKey: ['family-activities', familyId, filters],
    queryFn: () => activityFeedService.getFamilyActivities(familyId, 10, 0, filters),
    enabled: !!familyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const activities = response?.data || [];

  const activityTypeOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'recipe_created', label: 'Recipes Created' },
    { value: 'recipe_updated', label: 'Recipes Updated' },
    { value: 'meal_planned', label: 'Meals Planned' },
    { value: 'meal_updated', label: 'Meals Updated' },
    { value: 'feedback_added', label: 'Feedback Added' },
    { value: 'shopping_list_generated', label: 'Shopping Lists Generated' },
    { value: 'wellness_report_generated', label: 'Wellness Reports Generated' },
    { value: 'note_created', label: 'Notes Created' },
    { value: 'note_updated', label: 'Notes Updated' },
    { value: 'note_deleted', label: 'Notes Deleted' }
  ];

  const userOptions = useMemo(() => [
    { value: 'all', label: 'All Family Members' },
    ...familyMembers.map(member => ({
      value: member.id,
      label: `${member.first_name} ${member.last_name}`
    }))
  ], [familyMembers]);

  const handleFilterChange = (key: keyof ActivityFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === 'all' ? undefined : value
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !response?.success) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-orange-600 mb-4">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm">Unable to load family activities at this time.</p>
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* Filter Toggle Button */}
      <div className="px-6 pt-4 pb-2 border-b">
        <Button 
          onClick={() => setShowFilters(!showFilters)}
          variant="ghost" 
          size="sm"
          className="flex items-center gap-2 w-full justify-between"
        >
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </div>
          {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {/* Collapsible Filter Section */}
      {showFilters && (
        <div className="px-6 py-4 border-b bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Activity Type</label>
              <Select
                value={filters.activityType || 'all'}
                onValueChange={(value) => handleFilterChange('activityType', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activityTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Family Member</label>
              <Select
                value={filters.userId || 'all'}
                onValueChange={(value) => handleFilterChange('userId', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button onClick={clearFilters} variant="ghost" size="sm">
            Clear Filters
          </Button>
        </div>
      )}

      <CardContent className="p-0">
        {activities.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500 mb-2">No family activities yet</p>
            <p className="text-sm text-gray-400">
              Start creating recipes, planning meals, generating shopping lists, and checking wellness insights to see your family's collaborative cooking journey!
            </p>
          </div>
        ) : (
          <div>
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
